import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Badge,
  Icon,
  Link,
  TrafficBadge,
  Heading,
} from "@ogt/ui";
import { useEffect, useState } from "react";
import {
  TrafficFilter,
  TrafficInfo,
  TrafficInfoResponse,
  TrafficLine,
} from "src/types/FormTypes";

import { buildSpaUrl } from "src/config/urls";

// API base URL
const apiBaseUrl =
  process.env.REACT_APP_TPS_API_BASE_URL?.replace(/\/$/, "") ?? "";
const TRAFFIC_INFO_URL = `${apiBaseUrl}/api/v1/TrafficInfo`;

// Get display name for a traffic line
function getLineDisplay(line: TrafficLine) {
  const name = line.Ogt?.OgtLineNamePreformatted ?? line.LineName;

  if (/krösatåg|östgötapendeln/i.test(name)) {
    return <Icon name="train" size={16} className="mr-2" />;
  }

  return (
    <Badge
      className="no-underline mr-2"
      shape="default"
      state="default"
      variant="green"
    >
      {line.LineName}
    </Badge>
  );
}

// Fetch traffic info from API
function useTrafficInfo(filter: TrafficFilter) {
  const [data, setData] = useState<TrafficInfo[]>([]);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!apiBaseUrl) return;

    setLoading(true);

    const activeTypes = Object.entries(filter.modes)
      .filter(([, checked]) => checked)
      .map(([key]) => key);

    // If no active types, clear data and return
    if (activeTypes.length === 0) {
      setData([]);
      setLoading(false);
      return;
    }

    const params = new URLSearchParams();

    if (activeTypes.length > 0) {
      params.set("trafficTypeFilters", activeTypes.join(","));
    }

    if (filter.time === "today") {
      params.set("timeSpan", "Today");
    }

    if (filter.time === "upcoming") {
      params.set("timeSpan", "Upcoming");
    }

    fetch(`${TRAFFIC_INFO_URL}?${params.toString()}`)
      .then((res) => {
        if (!res.ok) {
          throw new Error("Failed to fetch traffic info");
        }
        return res.json();
      })
      .then((response: TrafficInfoResponse) => {
        setData(response.TrafficEvents);
        setLastUpdated(new Date(response.LastUpdated));
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [filter]);

  return { data, lastUpdated, loading };
}

// Map API data to TrafficBadge meta
function toTrafficMetaFromApi(item: TrafficInfo) {
  const type = item.TrafficTypes?.[0];

  return {
    iconCss: type?.OgtTrafficTypeIconCssClass ?? "ogt-service-bus",
    groupCss: type?.OgtTrafficTypeGroupCssClass ?? "TÄTO",
    title: type?.Label ?? "Trafikinformation",
  };
}

// Default meta if API data is missing
function toTrafficMeta() {
  return {
    iconCss: "ogt-service-bus",
    groupCss: "TÄTO",
    title: "Tätortstrafik",
  };
}
const meta = toTrafficMeta();

// Determine if the item was published or updated last
function getPublishedLabel(item: TrafficInfo) {
  const added = new Date(item.AddedToDatabase).getTime();
  const updated = new Date(item.UpdatedInDatabase).getTime();

  if (updated > added) {
    return {
      label: "Uppdaterad",
      date: new Date(item.UpdatedInDatabase),
    };
  }

  return {
    label: "Publicerad",
    date: new Date(item.AddedToDatabase),
  };
}

// List component to display traffic info
export default function List({ filter }: { filter: TrafficFilter }) {
  const { data, lastUpdated, loading } = useTrafficInfo(filter);

  lastUpdated?.toLocaleString("sv-SE", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });

  // Format list of IDs in Swedish
  function formatIdsSv(ids: number[]) {
    if (!ids || ids.length === 0) return "";

    if (ids.length === 1) return ids[0].toString();

    if (ids.length === 2) return `${ids[0]} och ${ids[1]}`;

    return `${ids.slice(0, -1).join(", ")} och ${ids[ids.length - 1]}`;
  }

  // Format date range in Swedish format
  function formatDateRangeFullSv(from: string, to: string) {
    function format(dateString: string) {
      const date = new Date(dateString);

      const d = date.toLocaleDateString("sv-SE", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });

      const t = date.toLocaleTimeString("sv-SE", {
        hour: "2-digit",
        minute: "2-digit",
      });

      return `${d} kl. ${t}`;
    }

    return `Från ${format(from)} till ${format(to)}`;
  }

  // Format date and time in Swedish format
  function formatDateTimeFullSv(date: Date) {
    const d = date.toLocaleDateString("sv-SE");
    const t = date.toLocaleTimeString("sv-SE", {
      hour: "2-digit",
      minute: "2-digit",
    });

    return `${d} kl.${t}`;
  }

  function hasValidPeriod(from?: string, to?: string) {
    if (!from || !to) return false;

    const fromTime = new Date(from).getTime();
    const toTime = new Date(to).getTime();

    // NaN-check + order
    if (!Number.isFinite(fromTime) || !Number.isFinite(toTime)) return false;

    return toTime >= fromTime;
  }

  // live message for screen readers

  const [liveMessage, setLiveMessage] = useState("");

  // screen reader - text
  useEffect(() => {
    if (loading) return;

    setLiveMessage("");

    const timeoutId = window.setTimeout(() => {
      setLiveMessage(`Visar ${data.length} av ${data.length} händelser.`);
    }, 50);

    return () => window.clearTimeout(timeoutId);
  }, [loading, data.length, filter]);

  return (
    <>
      <div className="sr-only" aria-live="polite" aria-atomic="true">
        {liveMessage}
      </div>

      {loading ? (
        <p>Laddar trafikinformation…</p>
      ) : (
        <>
          <div className="mb-6">
            <Heading className="mb-4">Aktuella händelser</Heading>

            {lastUpdated && (
              <p className="mb-6">
                Trafikinformationen uppdaterades{" "}
                {formatDateTimeFullSv(lastUpdated)}
              </p>
            )}
          </div>

          <hr />

          <p className="mb-6">
            Visar {data.length} av {data.length} händelser
          </p>

          <Accordion type="multiple">
            {data.map((item) => {
              const meta = toTrafficMetaFromApi(item);
              const { label, date } = getPublishedLabel(item);

              const ids = item.Ids ?? [];
              const idKey = ids.join("-");

              return (
                <AccordionItem key={idKey} value={`item-${idKey}`}>
                  <AccordionTrigger>
                    <div>

                      <div className="flex items-start mb-5 gap-2">
                        <div className="w-8 shrink-0 mt-1">
                          <TrafficBadge meta={meta} />
                        </div>

                        <Heading
                          as="h3"
                          className="leading-6 text-size-small mb-0 mt-1.75!"
                        >
                          {item.Heading}
                        </Heading>
                      </div>
                      
                      <div className="flex flex-col">
                        {hasValidPeriod(item.From, item.To) && (
                          <div className="md:ml-10 mb-6 md:mb-8 flex items-center gap-2">
                            <Icon
                              name="calendar"
                              size={16}
                              className="shrink-0"
                              aria-hidden="true"
                            />
                            <p className="font-normal text-sm mb-0">
                              {formatDateRangeFullSv(item.From, item.To)}
                            </p>
                          </div>
                        )}

                        <div className="flex">
                          <p className="md:ml-10 mb-2 font-normal text-sm">
                            {label} {formatDateTimeFullSv(date)}
                          </p>
                        </div>
                      </div>
                    </div>

                  </AccordionTrigger>

                  <AccordionContent>
                    <p className="mb-4!">{item.Details}</p>

                    {hasValidPeriod(item.From, item.To) && (
                      <>
                        <Heading
                          as="h4"
                          className="mb-0 text-size-small font-semibold"
                        >
                          Gäller under perioden
                        </Heading>
                        <p>{formatDateRangeFullSv(item.From, item.To)}</p>
                      </>
                    )}

                    {item.Stops && item.Stops.length > 0 && (
                      <div className="mt-6">
                        <Heading
                          as="h4"
                          className="mb-0 text-size-small font-semibold"
                        >
                          Hållplatser som påverkas
                        </Heading>

                        <ul className="p-0 m-0">
                          {item.Stops.map((stop) => {
                            return (
                              <li key={stop.Id} className="group">
                                <Link
                                  href={buildSpaUrl("stops", {
                                    "": stop.Id,
                                    stop: stop.Name.trim(),
                                  })}
                                  target="_self"
                                  className="flex items-center py-2 group-last:pb-0"
                                  underline="none"
                                >
                                  <div className="flex items-center">
                                    <span className="no-underline">
                                      <Badge
                                        className="no-underline mr-2"
                                        shape="default"
                                        state="default"
                                        variant="red"
                                      >
                                        H
                                      </Badge>
                                    </span>
                                    {stop.Name}
                                    <Icon
                                      name="chevron-right"
                                      size={16}
                                      className="ml-2 shrink-0"
                                      aria-hidden="true"
                                    />
                                  </div>
                                </Link>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}

                    {item.Lines && item.Lines.length > 0 && (
                      <div className="mt-6">
                        <Heading
                          as="h4"
                          className="mb-0 text-size-small font-semibold"
                        >
                          Linjer som påverkas
                        </Heading>

                        <ul className="p-0 m-0">
                          {item.Lines.map((line) => {
                            const isTrain = /krösatåg|östgötapendeln/i.test(
                              line.Ogt?.OgtLineNamePreformatted ?? line.LineName
                            );

                            return (
                              <li key={line.LineNrReal} className="group">
                                <Link
                                  href={buildSpaUrl("lines", {
                                    lineNumber: line.LineName,
                                  })}
                                  target="_self"
                                  className="flex items-center py-2 group-last:pb-0"
                                  underline="none"
                                >
                                  <div className="flex items-center">
                                    <span className="no-underline">
                                      <Badge
                                        className="no-underline mr-2"
                                        shape="default"
                                        state="default"
                                        variant="green"
                                      >
                                        {isTrain ? (
                                          <Icon
                                            name="train"
                                            size={16}
                                            aria-hidden="true"
                                          />
                                        ) : (
                                          line.LineName
                                        )}
                                      </Badge>
                                    </span>
                                    <span className="text-size-small">
                                      {line.Ogt.OgtLineNamePreformatted}
                                    </span>
                                    <Icon
                                      name="chevron-right"
                                      size={16}
                                      className="ml-2 shrink-0"
                                      aria-hidden="true"
                                    />
                                  </div>
                                </Link>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}

                    <p className="mt-8!">TIMS ID: {formatIdsSv(ids)}</p>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </>
      )}
    </>
  );
}
